import { Drawer } from '@mantine/core'
import type React from 'react'
import { Typography } from './Typography'

export type ModalSize = 'full' | 'half90' | 'half50' | 'half30' | 'half20' | 'fit'

export type ModalProps = {
  children?: React.ReactNode
  /** モーダルの開閉状態 */
  opened: boolean
  /** モーダルのサイズ（デフォルト: 'full'） */
  size?: ModalSize
  /** タイトルテキスト */
  title?: string
  /** true: タイトル固定ヘッダー, false: スワイプハンドル表示（デフォルト: true） */
  fixedHeader?: boolean
  /** 閉じるボタン・ESCキー押下時のコールバック */
  onClose?: () => void
  /** 背景オーバーレイクリック時のコールバック（未指定時は onClose と同じ） */
  onOverlayClick?: () => void
}

const SIZE_MAP: Record<ModalSize, string> = {
  full: 'auto',
  half90: '90vh',
  half50: '50vh',
  half30: '30vh',
  half20: '25vh',
  fit: 'auto',
}

const HANDLE_HEIGHT = 28 // handle(6px) + margin(8px top + 14px bottom)

export function Modal({
  children,
  opened,
  size = 'full',
  title,
  fixedHeader = true,
  onClose,
  onOverlayClick,
}: ModalProps) {
  const handleClose = onClose ?? (() => {})
  const handleOverlayClick = onOverlayClick ?? handleClose

  return (
    <Drawer.Root
      opened={opened}
      onClose={handleClose}
      position="bottom"
      size={SIZE_MAP[size]}
      styles={{
        content: {
          borderRadius: '16px 16px 0 0',
          display: 'flex',
          flexDirection: 'column',
          ...(size === 'full' ? { maxHeight: '90vh' } : {}),
          ...(size === 'half30' ? { minHeight: '240px' } : {}),
          ...(size === 'fit' ? { maxHeight: '50vh', height: 'fit-content' } : {}),
        },
        header: {
          background: '#ffffff',
          paddingTop: fixedHeader ? '10px' : `${HANDLE_HEIGHT + 10}px`,
          paddingBottom: '10px',
          paddingLeft: '4px',
          paddingRight: '4px',
          minHeight: fixedHeader ? '48px' : `${HANDLE_HEIGHT + 48}px`,
          justifyContent: 'center',
          position: 'relative',
        },
        title: {
          width: '100%',
          textAlign: 'center',
          paddingLeft: '44px',
          paddingRight: '44px',
        },
        body: {
          ...(size === 'fit' ? { flex: 'none' } : { flex: 1 }),
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch' as React.CSSProperties['WebkitOverflowScrolling'],
          padding: 0,
        },
        close: {
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '24px',
          height: '24px',
        },
      }}
    >
      <Drawer.Overlay onClick={handleOverlayClick} />
      <Drawer.Content>
        {!fixedHeader && (
          <div
            aria-hidden="true"
            style={{
              width: 48,
              height: 6,
              background: '#d9d9d9',
              borderRadius: 6,
              margin: '8px auto 14px',
              flexShrink: 0,
            }}
          />
        )}
        <Drawer.Header>
          {title && (
            <Drawer.Title>
              <Typography
                variant="subtitle1"
                component="p"
                className="line-clamp-2"
                style={{ color: '#000000', textAlign: 'center' }}
              >
                {title}
              </Typography>
            </Drawer.Title>
          )}
          {onClose && <Drawer.CloseButton />}
        </Drawer.Header>
        <Drawer.Body>{children}</Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  )
}
